import React, { useCallback, useReducer } from 'react';
import { createDishReducer } from './createDishReducer';
import { CreateDishActionTypes } from './createDishActions';
import {
	TFileId,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	TLanguageIDs,
	IDefaultLanguage,
	ITotalPrice,
	IItemForGetShortDishInfo,
} from 'types';
import {
	CreateDishContext,
	createDishInitialState,
	ICreateDishErrorResponseData,
	TCreateDishVisibleForm,
	TTypeOfCreateDishModalWindow,
	IIDsShowInComboDishes,
} from './createDishContext';
import { v4 as uuidv4 } from 'uuid';

const CreateDishState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createDishReducer, createDishInitialState);

	const changeVisibleForm = useCallback((value: TCreateDishVisibleForm) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateDishActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const changeDishName = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_DISH_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_CUSTOMER,
			payload: {
				customer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfoWithStatus[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setRestaurantsForPrice = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_RESTAURANTS_FOR_PRICE,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const addPrices = useCallback((value: ITotalPrice[]) => {
		dispatch({
			type: CreateDishActionTypes.ADD_PRICE,
			payload: {
				prices: value,
			},
		});
	}, []);

	const removePrice = useCallback(
		(value: string[]) => {
			const updatedTotalPrices = state.totalPrices.filter((el) => !value.includes(el.restaurantId));
			const updatedRestaurantsForPrices = state.restaurantsForPrice.filter((el) => !value.includes(el));
			const updatedIsDisabledInRestaurants = state.isDisabledInRestaurants.filter((el) => !value.includes(el));
			const updatedRestaurantWithTurnOffComment = state.restaurantWithTurnOffComment.filter(
				(el) => !value.includes(el),
			);
			const updatedRestaurantWithRequiredComment = state.restaurantWithRequiredComment.filter(
				(el) => !value.includes(el),
			);
			dispatch({
				type: CreateDishActionTypes.REMOVE_PRICE,
				payload: {
					updatedTotalPrices,
					updatedRestaurantsForPrices,
					updatedIsDisabledInRestaurants,
					restaurantWithTurnOffComment: updatedRestaurantWithTurnOffComment,
					restaurantWithRequiredComment: updatedRestaurantWithRequiredComment,
				},
			});
		},
		[
			state.totalPrices,
			state.restaurantsForPrice,
			state.isDisabledInRestaurants,
			state.restaurantWithTurnOffComment,
			state.restaurantWithRequiredComment,
		],
	);

	const setRestaurantWithTurnOffComment = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_RESTAURANT_WITH_TURN_OFF_COMMENT,
			payload: {
				restaurantWithTurnOffComment: value,
			},
		});
	}, []);

	const setRestaurantWithRequiredComment = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_RESTAURANT_WITH_REQUIRED_COMMENT,
			payload: {
				restaurantWithRequiredComment: value,
			},
		});
	}, []);

	const changeShortDescription = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_DISH_DESCRIPTION,
			payload: {
				shortDescription: value,
			},
		});
	}, []);

	const setDisabledInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_DISH_IS_DISABLED,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateDishActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				categories: value,
			},
		});
	}, []);

	const setSuggestedDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_SUGGESTED_DISH_CATEGORY,
			payload: {
				suggestedDishCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_SUGGESTED_DISH_SUB_CATEGORIES,
			payload: {
				suggestedDishSubCategories: value,
			},
		});
	}, []);

	const setSuggestedDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_SUGGESTED_DISH_SUB_CATEGORY,
			payload: {
				suggestedDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedSuggestedDishes.includes(el.id));
			const filteredFetchedSuggestedDishes = state.fetchedSuggestedDishes.filter((el) =>
				state.selectedSuggestedDishes.includes(el.id),
			);
			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_SUGGESTED_DISHES,
				payload: {
					suggestedDishes: [...filteredValue, ...filteredFetchedSuggestedDishes],
				},
			});
		},
		[state.selectedSuggestedDishes, state.fetchedSuggestedDishes],
	);

	const setSuggestedDishes = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_SUGGESTED_DISHES,
			payload: {
				suggestedDishes: value,
			},
		});
	}, []);

	const removeSuggestedDish = useCallback(
		(value: string) => {
			const updatedSelectedSuggestedDishes = state.selectedSuggestedDishes.filter((el) => el !== value);
			dispatch({
				type: CreateDishActionTypes.REMOVE_SUGGESTED_DISH,
				payload: {
					updatedSelectedSuggestedDishes,
				},
			});
		},
		[state.selectedSuggestedDishes],
	);

	const setAdditionalDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_ADDITIONAL_DISH_CATEGORY,
			payload: {
				additionalDishCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_ADDITIONAL_DISH_SUB_CATEGORIES,
			payload: {
				additionalDishSubCategories: value,
			},
		});
	}, []);

	const setAdditionalDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_ADDITIONAL_DISH_SUB_CATEGORY,
			payload: {
				additionalDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedAdditionalDishes.includes(el.id));
			const filteredFetchedAdditionalDishes = state.fetchedAdditionalDishes.filter((el) =>
				state.selectedAdditionalDishes.includes(el.id),
			);
			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_ADDITIONAL_DISHES,
				payload: {
					additionalDishes: [...filteredValue, ...filteredFetchedAdditionalDishes],
				},
			});
		},
		[state.selectedAdditionalDishes, state.fetchedAdditionalDishes],
	);

	const setAdditionalDishes = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_ADDITIONAL_DISHES,
			payload: {
				additionalDishes: value,
			},
		});
	}, []);

	const removeAdditionalDish = useCallback(
		(value: string) => {
			const updatedSelectedAdditionalDishes = state.selectedAdditionalDishes.filter((el) => el !== value);
			dispatch({
				type: CreateDishActionTypes.REMOVE_ADDITIONAL_DISH,
				payload: {
					updatedSelectedAdditionalDishes,
				},
			});
		},
		[state.selectedAdditionalDishes],
	);

	const setDishModificationCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_DISH_MODIFICATION_CATEGORY,
			payload: {
				dishModificationCategory: value,
			},
		});
	}, []);

	const setFetchedDishModificationSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_DISH_MODIFICATION_SUB_CATEGORIES,
			payload: {
				dishModificationSubCategories: value,
			},
		});
	}, []);

	const setDishModificationSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_DISH_MODIFICATION_SUB_CATEGORY,
			payload: {
				dishModificationSubCategory: value,
			},
		});
	}, []);

	const setFetchedDishModifications = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedDishModifications.includes(el.id));
			const filteredFetchedDishModifications = state.fetchedDishModifications.filter((el) =>
				state.selectedDishModifications.includes(el.id),
			);
			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_DISH_MODIFICATIONS,
				payload: {
					dishModifications: [...filteredValue, ...filteredFetchedDishModifications],
				},
			});
		},
		[state.selectedDishModifications, state.fetchedDishModifications],
	);

	const setDishModifications = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_DISH_MODIFICATIONS,
			payload: {
				dishModifications: value,
			},
		});
	}, []);

	const removeDishModification = useCallback(
		(value: string) => {
			const updatedSelectedDishModifications = state.selectedDishModifications.filter((el) => el !== value);
			const copyIDsShowInComboDishes = { ...state.IDsShowInComboDishes };
			delete copyIDsShowInComboDishes[value];
			dispatch({
				type: CreateDishActionTypes.REMOVE_DISH_MODIFICATION,
				payload: {
					updatedSelectedDishModifications,
					IDsShowInComboDishes: copyIDsShowInComboDishes,
				},
			});
		},
		[state.selectedDishModifications, state.IDsShowInComboDishes],
	);

	const setIDsShowInComboDishes = useCallback((value: IIDsShowInComboDishes) => {
		dispatch({
			type: CreateDishActionTypes.SET_IDS_SHOW_IN_COMBO_DISHES,
			payload: {
				IDsShowInComboDishes: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: CreateDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateDishModalWindow) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: ICreateDishErrorResponseData | null) => {
		dispatch({
			type: CreateDishActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateDishActionTypes.RESET_FIELDS }), []);

	return (
		<CreateDishContext.Provider
			value={{
				changeVisibleForm,
				setFileId,
				changeDishName,
				setFetchedCustomers,
				setCustomer,
				setFetchedRestaurants,
				setRestaurantsForPrice,
				changePrice,
				addPrices,
				removePrice,
				setRestaurantWithTurnOffComment,
				setRestaurantWithRequiredComment,
				changeShortDescription,
				setDisabledInRestaurants,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedCategories,
				setSuggestedDishCategory,
				setFetchedSuggestedDishSubCategories,
				setSuggestedDishSubCategory,
				setFetchedSuggestedDishes,
				setSuggestedDishes,
				removeSuggestedDish,
				setAdditionalDishCategory,
				setFetchedAdditionalDishSubCategories,
				setAdditionalDishSubCategory,
				setFetchedAdditionalDishes,
				setAdditionalDishes,
				removeAdditionalDish,
				setDishModificationCategory,
				setFetchedDishModificationSubCategories,
				setDishModificationSubCategory,
				setFetchedDishModifications,
				setDishModifications,
				removeDishModification,
				setIDsShowInComboDishes,
				changeExternalId,
				generateUniqueExternalId,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				name: state.name,
				fetchedCustomers: state.fetchedCustomers,
				selectedCustomer: state.selectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				restaurantsForPrice: state.restaurantsForPrice,
				price: state.price,
				totalPrices: state.totalPrices,
				restaurantWithTurnOffComment: state.restaurantWithTurnOffComment,
				restaurantWithRequiredComment: state.restaurantWithRequiredComment,
				shortDescription: state.shortDescription,
				isDisabledInRestaurants: state.isDisabledInRestaurants,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				fetchedCategories: state.fetchedCategories,
				selectedSuggestedDishCategory: state.selectedSuggestedDishCategory,
				fetchedSuggestedDishSubCategories: state.fetchedSuggestedDishSubCategories,
				selectedSuggestedDishSubCategory: state.selectedSuggestedDishSubCategory,
				fetchedSuggestedDishes: state.fetchedSuggestedDishes,
				selectedSuggestedDishes: state.selectedSuggestedDishes,
				selectedAdditionalDishCategory: state.selectedAdditionalDishCategory,
				fetchedAdditionalDishSubCategories: state.fetchedAdditionalDishSubCategories,
				selectedAdditionalDishSubCategory: state.selectedAdditionalDishSubCategory,
				fetchedAdditionalDishes: state.fetchedAdditionalDishes,
				selectedAdditionalDishes: state.selectedAdditionalDishes,
				selectedDishModificationCategory: state.selectedDishModificationCategory,
				fetchedDishModificationSubCategories: state.fetchedDishModificationSubCategories,
				selectedDishModificationSubCategory: state.selectedDishModificationSubCategory,
				fetchedDishModifications: state.fetchedDishModifications,
				selectedDishModifications: state.selectedDishModifications,
				IDsShowInComboDishes: state.IDsShowInComboDishes,
				externalId: state.externalId,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</CreateDishContext.Provider>
	);
};

export default CreateDishState;
